/**
 * DN Size Guide Drawer  ·  assets/dn-size-guide.js
 *
 * Opens/closes the right-side size guide drawer and handles tab switching.
 *
 * TRIGGERS — add any of these to your existing size guide link:
 *   data-size-guide-open         ← recommended attribute
 *   class="dn-size-guide-trigger"
 *   class="size-guide-link"      ← if your theme already uses this class
 *
 * PROGRAMMATIC API (from other scripts):
 *   window.DNSizeGuide.open()
 *   window.DNSizeGuide.close()
 *   window.DNSizeGuide.switchTab(n)   // n = 1-based index
 */

(function () {
  'use strict';

  const DRAWER_ID    = 'dn-size-guide-drawer';
  const OPEN_CLASS   = 'is-open';
  const ACTIVE_CLASS = 'is-active';

  // Selectors that trigger the drawer open
  const TRIGGER_SELECTORS = [
    '[data-size-guide-open]',
    '.dn-size-guide-trigger',
    '.size-guide-link',
  ].join(', ');

  // Keep reference to the element that opened the drawer (for focus restore)
  let _lastFocus = null;

  // ── Helpers ──────────────────────────────────────────────────────────────

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function isOpen() {
    const d = getDrawer();
    return d ? d.classList.contains(OPEN_CLASS) : false;
  }

  // ── Focus Trap ────────────────────────────────────────────────────────────

  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.hasAttribute('disabled') && !el.hasAttribute('aria-hidden'));
  }

  function trapFocus(e) {
    const drawer = getDrawer();
    if (!drawer || !isOpen()) return;

    const focusable = getFocusable(drawer);
    if (!focusable.length) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }

  // ── Open / Close ──────────────────────────────────────────────────────────

  function open() {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.add(OPEN_CLASS);
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus first focusable element inside drawer
    requestAnimationFrame(function () {
      const focusable = getFocusable(drawer);
      if (focusable.length) focusable[0].focus();
    });

    document.addEventListener('keydown', handleKeydown);
  }

  function close() {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.classList.remove(OPEN_CLASS);
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    document.removeEventListener('keydown', handleKeydown);

    // Restore focus to the triggering element
    if (_lastFocus && typeof _lastFocus.focus === 'function') {
      _lastFocus.focus();
      _lastFocus = null;
    }
  }

  // ── Tab Switching ─────────────────────────────────────────────────────────

  function switchTab(index) {
    const drawer = getDrawer();
    if (!drawer) return;

    const idx = String(index);

    // Update tab buttons
    drawer.querySelectorAll('[data-sgd-tab]').forEach(function (tab) {
      const active = tab.dataset.sgdTab === idx;
      tab.classList.toggle(ACTIVE_CLASS, active);
      tab.setAttribute('aria-selected', String(active));
    });

    // Update panels
    drawer.querySelectorAll('[data-sgd-panel]').forEach(function (panel) {
      const active = panel.dataset.sgdPanel === idx;
      panel.classList.toggle(ACTIVE_CLASS, active);
      if (active) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  // ── Keyboard Handler ──────────────────────────────────────────────────────

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    trapFocus(e);
  }

  // ── Event Delegation ──────────────────────────────────────────────────────

  function handleDocumentClick(e) {
    // Open trigger
    const trigger = e.target.closest(TRIGGER_SELECTORS);
    if (trigger) {
      e.preventDefault();
      _lastFocus = trigger;
      open();
      return;
    }

  // Close when clicking the X or anywhere outside the drawer panel
if (isOpen()) {
  const drawer = getDrawer();
  const panel = drawer.querySelector('.dn-sgd__panel');
  const closeEl = e.target.closest('[data-sgd-close]');

  if (
    closeEl ||
    (drawer.contains(e.target) && !panel.contains(e.target))
  ) {
    close();
    return;
  }
}

    // Tab buttons
    const tab = e.target.closest('[data-sgd-tab]');
    if (tab) {
      switchTab(tab.dataset.sgdTab);
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────

function init() {
  const drawer = getDrawer();
  if (!drawer) return;

  // Move drawer to <body> so position:fixed is never trapped
  // inside a section stacking context
  if (drawer.parentElement !== document.body) {
    document.body.appendChild(drawer);
  }

  document.addEventListener('click', handleDocumentClick);
  window.DNSizeGuide = { open: open, close: close, switchTab: switchTab };
}

  // ── Bootstrap ─────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();